<template>
  <div class="f-main bgImg container quiz-history-page">
    <div class="box" :style="wrapperStyle">
      <div class="title">
        <div class="hero-main">
          <div class="hero-left">
            <div class="hero-icon">
              <i class="el-icon-refresh-left" />
            </div>
            <div>
              <div class="hero-tags">
                <span class="tag">Grade {{ $route.query.gradeId || '-' }}</span>
                <span class="tag tag-accent">{{ $t("historical.title") }}</span>
              </div>
              <span class="historical-test">{{ $t("historical.title") }} — {{ userInfo.username }}</span>
              <span class="welcome">{{ $t("historical.welcome") }}</span>
            </div>
          </div>
          <div class="hero-right">
            <div class="hero-stats">
              <div class="stat-item">
                <span class="num">{{ totalTests }}</span>
                <span class="label">Total</span>
              </div>
              <div class="stat-item">
                <span class="num">{{ averageScore }}%</span>
                <span class="label">Avg Score</span>
              </div>
              <div class="stat-item">
                <span class="num">{{ bestScore }}%</span>
                <span class="label">Best</span>
              </div>
            </div>
            <div class="btns">
              <el-button icon="el-icon-back" @click="back">{{ $t("historical.back") }}</el-button>
            </div>
          </div>
        </div>
        <div class="hero-strip">
          <span class="crumb">Dashboard</span>
          <span class="sep">></span>
          <span class="crumb">Progress Report</span>
          <span class="sep">></span>
          <span class="crumb active">{{ $t("historical.title") }}</span>
        </div>
      </div>

      <div class="filter-bar">
        <div class="filter-left">
          <span class="filter-pill active">All ({{ totalTests }})</span>
          <span class="filter-pill">Number & Algebra</span>
          <span class="filter-pill">Geometry</span>
          <span class="filter-pill">Statistics</span>
        </div>
      </div>

      <div class="stat-cards">
        <div class="summary-card">
          <div class="sum-icon purple"><i class="el-icon-document" /></div>
          <div class="sum-copy"><p>{{ totalTests }}</p><span>Total Tests</span></div>
        </div>
        <div class="summary-card">
          <div class="sum-icon orange"><i class="el-icon-data-analysis" /></div>
          <div class="sum-copy"><p>{{ averageScore }}%</p><span>Avg Score</span></div>
        </div>
        <div class="summary-card">
          <div class="sum-icon green"><i class="el-icon-trophy" /></div>
          <div class="sum-copy"><p>{{ bestScore }}%</p><span>Best Score</span></div>
        </div>
        <div class="summary-card">
          <div class="sum-icon pink"><i class="el-icon-time" /></div>
          <div class="sum-copy"><p>{{ thisMonthCount }}</p><span>This Month</span></div>
        </div>
      </div>

      <div class="content">
        <div v-for="item in historicalData" :key="item.id" class="historical">
          <div class="card-top-line"></div>
          <div class="test-head">
            <div class="test-icon"><i class="el-icon-s-order" /></div>
            <div class="test-title-wrap">
              <div class="test-tags">
                <span class="mini-tag">{{ item.categoryName || 'Category' }}</span>
                <span class="mini-grade">Grade {{ $route.query.gradeId || '-' }}</span>
              </div>
              <p class="test-name" :title="item.testName">{{ item.testName || '-' }}</p>
            </div>
            <div class="score-box">
              <span class="score-num">{{ getScore(item) }}</span>
              <span class="score-level">{{ getScore(item) >= 80 ? 'Excellent' : 'Good' }}</span>
            </div>
          </div>

          <div class="meta-row">
            <span class="meta"><i class="el-icon-date" /> {{ item.createAt || '-' }}</span>
            <span class="meta"><i class="el-icon-time" /> {{ item.duration || '-' }}</span>
            <span class="meta"><i class="el-icon-tickets" /> {{ item.questionCount || '-' }} Qs</span>
            <span class="meta"><i class="el-icon-reading" /> {{ item.testType || 'AI Test' }}</span>
          </div>

          <div class="score-row">
            <span>Score</span>
            <span>{{ getScore(item) }}%</span>
          </div>
          <el-progress
            :percentage="getScore(item)"
            :show-text="false"
            :stroke-width="8"
            color="#22c55e"
            class="score-progress"
          />

          <div class="action-row">
            <el-button type="primary" class="report" @click="toReport(item.id)">{{ $t("historical.Report") }}</el-button>
            <el-button type="primary" class="questions" @click="toQuestions(item.id)">{{ $t("historical.Questions") }}</el-button>
          </div>
        </div>
      </div>
    </div>
    <div class="footer">{{ $t("common.footer") }}</div>
  </div>
</template>

<script>
import { historicalList } from '@/api/tutor/student'
import { mapState } from 'vuex'

export default {
  name: 'historical',
  props: {
    scale: {
      type: Number,
      required: 1
    }
  },
  computed: {
    ...mapState('user', {
      userInfo: (state) => state.userInfo
    }),
    wrapperStyle () {
      return {
        transform: `scale(${this.scale})`
      }
    },
    totalTests () {
      return this.historicalData.length
    },
    averageScore () {
      if (!this.historicalData.length) return 0
      const total = this.historicalData.reduce((sum, item) => sum + this.getScore(item), 0)
      return Math.round(total / this.historicalData.length)
    },
    bestScore () {
      if (!this.historicalData.length) return 0
      return Math.max(...this.historicalData.map((item) => this.getScore(item)))
    },
    thisMonthCount () {
      const now = new Date()
      return this.historicalData.filter((item) => {
        if (!item.createAt) return false
        const d = new Date(item.createAt.replace(/-/g, '/'))
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
      }).length
    }
  },
  data () {
    return {
      historicalData: []
    }
  },
  mounted () {
    this.init()
  },
  methods: {
    /**
     * 将分值转换为 0-100 百分比（支持 0-1 或 0-100）
     * @param {number|string} value 原始分值
     * @returns {number}
     */
    normalizePercentage (value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return 0
      if (n <= 1) return Math.round(n * 100)
      return Math.round(n)
    },
    /**
     * 历史记录得分（优先使用知识点得分率字段）
     * @param {Object} item 历史记录项
     * @returns {number}
     */
    getScore (item) {
      // 新接口字段：知识点维度得分率（0-100）
      if (item.knowledgePointScorePercentage != null && item.knowledgePointScorePercentage !== '') {
        return this.normalizePercentage(item.knowledgePointScorePercentage)
      }
      // 新接口字段：知识点得分/总分兜底
      if (item.knowledgePointEarnedPoints != null && item.knowledgePointTotalPoints != null && Number(item.knowledgePointTotalPoints) > 0) {
        return this.normalizePercentage((Number(item.knowledgePointEarnedPoints) / Number(item.knowledgePointTotalPoints)) * 100)
      }
      // 历史常见字段：总得分率
      if (item.scorePercentage != null && item.scorePercentage !== '') {
        return this.normalizePercentage(item.scorePercentage)
      }
      if (typeof item.score === 'number') return Math.round(item.score)
      if (typeof item.correctRate === 'number') {
        return item.correctRate <= 1 ? Math.round(item.correctRate * 100) : Math.round(item.correctRate)
      }
      if (typeof item.completionPercentage === 'number') {
        return item.completionPercentage <= 1 ? Math.round(item.completionPercentage * 100) : Math.round(item.completionPercentage)
      }
      return 0
    },
    init () {
      const params = {
        studentId: this.$route.query.studentId,
        knowledgePointId: this.$route.query.knowledgePointId
      }
      historicalList(params).then((res) => {
        if (res.code !== 200) {
          return false
        }
        this.historicalData = res.data
      })
    },
    back () {
      this.$router.go(-1)
    },
    home () {
      this.$router.push({
        path: '/parentOrTeacher'
      })
    },
    toQuestions (id) {
      this.$router.push({
        path: '/studentQuiz',
        query: {
          testRecordId: id,
          type: 'historical'
        }
      })
    },
    /**
     * 跳转到报告页并按历史记录ID加载报告
     * @param {number|string} id 测试记录ID
     */
    toReport (id) {
      this.$router.push({
        path: '/quizResults',
        query: {
          ...this.$route.query,
          testRecordId: id,
          type: 'historical'
        }
      })
    }
  }
}
</script>

<style scoped>
.quiz-history-page {
  --qh-purple: #7c4dff;
  --qh-purple-deep: #5b21b6;
  --qh-orange: #ff8a00;
}

.quiz-history-page.container {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: calc(100% - 90px);
  padding: 12px 16px;
  box-sizing: border-box;
  background: linear-gradient(180deg, #f3e8ff 0%, #ede7f6 45%, #e8e0f5 100%);
}

.page-tagline {
  width: 100%;
  max-width: 1480px;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  color: #fff;
  background: var(--qh-purple-deep);
  padding: 8px 16px;
  border-radius: 999px;
  margin-bottom: 12px;
}

.quiz-history-page .box {
  width: 100%;
  max-width: 1443px;
  min-height: 760px;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
}

.title {
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(91, 33, 182, 0.2);
  margin-bottom: 16px;
}

.hero-main {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  background: linear-gradient(115deg, #7c4dff 0%, #6366f1 45%, #2563eb 100%);
}

.hero-left {
  display: flex;
  gap: 12px;
  align-items: center;
  min-width: 0;
}

.hero-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.hero-tags {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
}

.tag {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: rgba(255, 255, 255, 0.2);
}

.tag-accent {
  background: rgba(255, 138, 0, 0.92);
}

.historical-test {
  display: block;
  font-weight: 900;
  font-size: 34px;
  color: #ffffff;
}

.welcome {
  display: block;
  font-weight: 500;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.88);
  margin-top: 2px;
}

.hero-right {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.hero-stats {
  display: flex;
  gap: 8px;
}

.stat-item {
  min-width: 78px;
  height: 60px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.stat-item .num {
  font-size: 26px;
  font-weight: 900;
  line-height: 1;
}

.stat-item .label {
  font-size: 11px;
  margin-top: 4px;
  opacity: 0.9;
}

.btns :deep(.el-button) {
  border-radius: 999px;
  height: 40px;
  padding: 0 18px;
  font-weight: 800;
  border: none;
}

.btns :deep(.el-button:first-child) {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.btns :deep(.el-button:last-child) {
  background: var(--qh-orange);
  color: #fff;
}

.hero-strip {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: rgba(49, 20, 120, 0.35);
  color: rgba(255, 255, 255, 0.9);
  font-weight: 700;
  font-size: 13px;
}

.crumb.active {
  color: #fff;
}

.filter-bar {
  height: 54px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.filter-left {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-pill {
  padding: 7px 14px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
}

.filter-pill.active {
  background: linear-gradient(90deg, #7c4dff 0%, #5b21b6 100%);
  color: #fff;
}

.sort-label {
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
}

.stat-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 14px;
}

.summary-card {
  height: 76px;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 0 14px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.sum-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.sum-icon.purple { background: linear-gradient(145deg, #7c4dff 0%, #5b21b6 100%); }
.sum-icon.orange { background: linear-gradient(145deg, #f59e0b 0%, #f97316 100%); }
.sum-icon.green { background: linear-gradient(145deg, #14b8a6 0%, #0d9488 100%); }
.sum-icon.pink { background: linear-gradient(145deg, #ec4899 0%, #db2777 100%); }

.sum-copy p {
  margin: 0;
  font-size: 26px;
  font-weight: 900;
  line-height: 1;
  color: #0f172a;
}

.sum-copy span {
  font-size: 13px;
  color: #64748b;
  font-weight: 700;
}

.content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.historical {
  position: relative;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  padding: 16px;
}

.card-top-line {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 4px;
  border-radius: 16px 16px 0 0;
  background: linear-gradient(90deg, #7c4dff 0%, #22c55e 100%);
}

.test-head {
  display: flex;
  gap: 10px;
  align-items: center;
}

.test-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #eef2ff;
  color: #6366f1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.test-title-wrap {
  flex: 1;
  min-width: 0;
}

.test-tags {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
}

.mini-tag {
  padding: 2px 8px;
  border-radius: 999px;
  background: #ede9fe;
  color: #5b21b6;
  font-size: 12px;
  font-weight: 800;
}

.mini-grade {
  color: #94a3b8;
  font-size: 13px;
  font-weight: 700;
}

.test-name {
  margin: 0;
  font-size: 34px;
  font-weight: 900;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.score-box {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: linear-gradient(145deg, #f59e0b 0%, #f97316 100%);
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.score-num {
  font-size: 25px;
  font-weight: 900;
  line-height: 1;
}

.score-level {
  margin-top: 3px;
  font-size: 11px;
  font-weight: 700;
}

.meta-row {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 700;
}

.meta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.score-row {
  margin-top: 12px;
  display: flex;
  justify-content: space-between;
  font-size: 16px;
  font-weight: 800;
  color: #475569;
}

.score-progress {
  margin-top: 6px;
}

.score-progress :deep(.el-progress-bar__outer) {
  border-radius: 999px;
  background-color: #e2e8f0;
}

.score-progress :deep(.el-progress-bar__inner) {
  border-radius: 999px;
}

.action-row {
  margin-top: 12px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.action-row :deep(.el-button) {
  width: 100%;
  height: 40px;
  border-radius: 999px;
  font-weight: 800;
  margin-left: 0;
}

.report {
  background: linear-gradient(90deg, #7c4dff 0%, #5b21b6 100%);
  border-color: transparent;
}

.questions {
  background: #fff;
  color: #7c4dff;
  border: 1px solid #ddd6fe;
}

.quiz-history-page .footer {
  margin-top: 16px;
  font-weight: 400;
  font-size: 11px;
  color: #94a3b8;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

@media (max-width: 1080px) {
  .stat-cards,
  .content {
    grid-template-columns: 1fr;
  }
}
</style>
